// ─── QR des emplacements d'inventaire ────────────────────────────────────────
// • Génère un QR par emplacement (« placard ») : on le colle sur le meuble, puis
//   on le scanne dans l'inventaire pour voir tout ce qui est rangé dedans.
// • Le QR encode une chaîne courte : MOPCAL-EMP:<inventaireId>:<emplacementId>.
//   Préfixe INCHANGÉ volontairement : les étiquettes déjà collées par MoPCal
//   restent lisibles par My Inventory, et réciproquement.
// • Planche imprimable (HTML → expo-print sur mobile / impression navigateur sur
//   web), une carte par emplacement (QR + emoji + nom + chemin), à découper.
//
// La génération du QR utilise `qrcode` en important DIRECTEMENT le cœur + le
// rendu SVG (pur JS) — on évite `qrcode/lib/browser` qui tire le rendu canvas
// (document / <canvas> absents en natif). Fonctionne donc identique web + mobile.
import { Platform } from 'react-native';
import QRCodeCore from 'qrcode/lib/core/qrcode';
import SvgRenderer from 'qrcode/lib/renderer/svg-tag';
import { imprimerHTML } from '@mop/core';
import { aplatirArbre, cheminDe } from './inventaires';

const PREFIXE = 'MOPCAL-EMP';

// Chaîne encodée dans le QR d'un emplacement.
export function codeEmplacement(inventaireId, emplacementId) {
  return `${PREFIXE}:${inventaireId || ''}:${emplacementId || ''}`;
}

// Décode une chaîne scannée. Renvoie { inventaireId, emplacementId } ou null.
export function parseCodeEmplacement(code) {
  const s = String(code || '').trim();
  if (!s.startsWith(PREFIXE + ':')) return null;
  const parts = s.split(':');
  // MOPCAL-EMP : [0]=prefixe, [1]=inventaireId, [2]=emplacementId
  const emplacementId = parts[2] || '';
  if (!emplacementId) return null;
  return { inventaireId: parts[1] || '', emplacementId };
}

// Renvoie la balise <svg> (chaîne) du QR pour un texte donné.
export function qrSvg(texte, { taille = 160, marge = 1 } = {}) {
  const data = QRCodeCore.create(String(texte || ' '), { errorCorrectionLevel: 'M' });
  return SvgRenderer.render(data, {
    margin: marge,
    width: taille,
    color: { dark: '#111827', light: '#ffffff' },
  });
}

const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Planche HTML : une carte par emplacement (QR + emoji + nom + chemin).
export function genererPlancheQrHTML(inv, emplacements) {
  const plats = aplatirArbre(emplacements || []);
  const titre = (inv && inv.nom) || 'Inventaire';
  const cartes = plats.map((e) => {
    const svg = qrSvg(codeEmplacement(inv && inv.id, e.id), { taille: 150, marge: 1 });
    const chemin = cheminDe(emplacements || [], e.id) || '';
    return `
      <div class="carte">
        <div class="qr">${svg}</div>
        <div class="txt">
          <div class="nom">${esc(e.emoji || '📍')} ${esc(e.nom)}</div>
          <div class="chemin">${esc(chemin)}</div>
        </div>
      </div>`;
  }).join('');
  const corps = plats.length
    ? `<div class="grille">${cartes}</div>`
    : `<p class="vide">Aucun emplacement à imprimer. Crée d'abord des emplacements dans l'inventaire.</p>`;
  return `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>QR emplacements — ${esc(titre)}</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #111827; margin: 0; padding: 18px; }
      h1 { font-size: 18px; margin: 0 0 4px; }
      .sous { font-size: 12px; color: #6B7280; margin: 0 0 16px; }
      .grille { display: flex; flex-wrap: wrap; gap: 12px; }
      .carte { width: 200px; border: 1px dashed #9CA3AF; border-radius: 12px; padding: 10px; display: flex; flex-direction: column; align-items: center; page-break-inside: avoid; }
      .qr { line-height: 0; }
      .qr svg { width: 150px; height: 150px; display: block; }
      .txt { text-align: center; margin-top: 6px; width: 100%; }
      .nom { font-size: 14px; font-weight: 700; }
      .chemin { font-size: 11px; color: #6B7280; margin-top: 2px; word-break: break-word; }
      .vide { color: #6B7280; font-size: 13px; }
      @media print { .sous { display: none; } }
    </style></head><body>
      <h1>Emplacements — ${esc(titre)}</h1>
      <p class="sous">Découpe et colle chaque QR sur son rangement. Scan dans l'inventaire (bouton 📷) pour voir son contenu.</p>
      ${corps}
    </body></html>`;
}

// Produit et partage / imprime la planche de QR.
export async function imprimerPlancheQr(inv, emplacements) {
  const html = genererPlancheQrHTML(inv, emplacements);
  if (Platform.OS === 'web') return imprimerHTML(html);
  const Print = require('expo-print');
  const Sharing = require('expo-sharing');
  const { uri } = await Print.printToFileAsync({ html });
  if (await Sharing.isAvailableAsync()) {
    await Sharing.shareAsync(uri, { mimeType: 'application/pdf', dialogTitle: (inv && inv.nom) || 'QR emplacements', UTI: 'com.adobe.pdf' });
  }
  return true;
}
